import Button from 'UI/Button';
import DefaultModal from 'UI/Modals/DefaultModal';
import StyledText from 'UI/StyledText';
import httpClient from 'api/https-client';
import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { respondToWidth } from 'styles/general/respondTo';

type BillingStatusResponse = {
  hasBlockingInstallment: boolean;
  paymentUpdateUrl: string | null;
  blockingInstallments: Array<{
    id: string;
    status: string;
    iterationRemains: number;
    totalIterations: number;
  }>;
};

type Props = {
  children: ReactNode;
};

const BillingStatusGate: FC<Props> = ({ children }) => {
  const [status, setStatus] = useState<BillingStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await httpClient.get<BillingStatusResponse>('/customer/billingStatus');
      setStatus(data);
    } catch (err) {
      // Fail open — don't lock users out due to API error.
      setStatus({ hasBlockingInstallment: false, paymentUpdateUrl: null, blockingInstallments: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isBlocked = !loading && !!status?.hasBlockingInstallment;

  return (
    <>
      {children}
      <DefaultModal
        isOpen={isBlocked}
        onRequestClose={() => {
          // Intentional no-op — gate cannot be dismissed; user must update payment to proceed.
        }}
        showCloseButton={false}
        shouldCloseOnOverlayClick={false}
        title="Your installment payment is past due"
        containerCSS={ContainerCSS}
        modalCSS={ModalCSS}>
        <Body>
          We could not collect your most recent installment payment. To keep your access to the LMS, please update your
          payment method so we can collect the missed payments and continue with your plan.
        </Body>
        {status?.paymentUpdateUrl ? (
          <ActionButton
            variant="primary"
            size="middle"
            onClick={() => window.open(status.paymentUpdateUrl as string, '_blank', 'noopener,noreferrer')}>
            Update payment method
          </ActionButton>
        ) : (
          <Body>Please contact support to update your payment method.</Body>
        )}
        <SecondaryActionButton variant="secondary" size="middle" onClick={refresh} disabled={loading}>
          {loading ? 'Checking…' : 'I have updated my card — refresh status'}
        </SecondaryActionButton>
      </DefaultModal>
    </>
  );
};

export default BillingStatusGate;

const ContainerCSS = css`
  width: 520px;
  padding: 40px 56px;

  ${respondToWidth.sm`
    width: 100%;
    padding-left: 25px;
    padding-right: 25px;
  `}

  ${respondToWidth.s`
    padding: 32px 16px;
    border-radius: 20px 20px 0 0;
  `}
`;

const ModalCSS = css`
  max-width: 520px;

  ${respondToWidth.sm`
    width: 90%;
  `}

  ${respondToWidth.s`
    width: 100%;
    bottom: 0;
  `}
`;

const Body = styled(StyledText)`
  ${({ theme: { typography } }) => typography.body_large_regular_16};
  color: ${({ theme: { colors } }) => colors.neutrals[2]};
  text-align: center;
  margin-bottom: 24px;
`;

const ActionButton = styled(Button)`
  width: 100%;
`;

const SecondaryActionButton = styled(Button)`
  width: 100%;
  margin-top: 12px;
`;
